/*
 * The one place for the signal factors (Spec Verbleib 1a, Backtest, Stabilitäts- and Unabhängigkeitsprobe 04.11.2026).
 * Every text with a factor reads it from here; a new backtest changes this file and the date. `note` is shown visibly with the
 * value, never only in a tooltip. Copy in the frontend (src/enums/workflow-manager/QualitySignalFactors.ts), keep equal.
 * Basis: Abo-Basis, Abgang in 8 Wochen, Kinder ohne eingegangene Kündigung, saisonbereinigt. Keys = signal keys of the risk model
 * (stat-manager risk-model.rules). Correlations, no proven causes; companion signals are often a consequence, not the reason.
 */

export const QUALITY_SIGNAL_FACTORS_AS_OF = '04.11.2026';

/** Sentence after a factor in texts */
export const QUALITY_SIGNAL_FACTORS_SOURCE = `Stand ${QUALITY_SIGNAL_FACTORS_AS_OF}: Abgang in 8 Wochen, nur Kinder ohne eingegangene Kündigung, saisonbereinigt`;

export type QualitySignalFactorKey = 'unexcusedTwice' | 'excused3' | 'progressBack' | 'teacherChange' | 'teacherLost' | 'courseChange' | 'shortNotice' | 'ordinaryCancellation'
    | 'classmates3' | 'smallGroup' | 'firstMonths' | 'months6to12' | 'online' | 'age10to11' | 'calls' | 'familyMessages';

export type QualitySignalFactor = {
    name: string;
    /** "×1,8" or a range; null: no fixed factor */
    value: string | null;
    /** Visible beside the value: the value is not reliable */
    note?: string;
    companion?: boolean;
};

const FEW_CASES = 'wenige Fälle';
const NO_OWN_EFFECT = 'kein eigener Effekt belegt';

export const QUALITY_SIGNAL_FACTORS: Record<QualitySignalFactorKey, QualitySignalFactor> = {
    /** One signal: 2× in a row and ≥ 4 in 60 days are the same effect */
    unexcusedTwice: {name: 'Wiederholt unentschuldigt', value: '×1,8'},
    excused3: {name: '≥ 3 entschuldigt in 60 Tagen', value: '×1,6'},
    progressBack: {name: 'Fortschritt −1', value: '×1,4–2,8', note: FEW_CASES},
    /** ×1,4 in the backtest, ×1,03 in the model: not proven */
    teacherChange: {name: 'Lehrerwechsel', value: '×1,0–1,4', note: 'unsicher'},
    /** No factor as a forecast: the value was computed backwards, when it was known that the teacher left */
    teacherLost: {name: 'Lehrkraft fällt weg', value: null, note: 'Kinder einer Lehrkraft, die nach unentschuldigtem Ausfall wegfällt, gehen deutlich häufiger (rückblickend, wenige Fälle)'},
    courseChange: {name: 'Kurswechsel oder Zusammenlegung', value: null, note: NO_OWN_EFFECT},
    shortNotice: {name: 'Kurzfristiger Ausfall', value: '×1,5, kurzfristige Absage'},
    ordinaryCancellation: {name: 'Rechtzeitig abgesagter oder vertretener Ausfall', value: null, note: 'kein Effekt'},
    classmates3: {name: '≥ 3 Mitschüler:innen weg, Gruppe geschrumpft', value: null, note: NO_OWN_EFFECT},
    smallGroup: {name: 'Gruppe mit höchstens 2 Kindern', value: '×1,2–1,4'},
    firstMonths: {name: 'Erste 3 Monate', value: '×0,7–0,8'},
    months6to12: {name: 'Monate 6–12', value: '×1,2'},
    online: {name: 'Online-Unterricht', value: '×0,5', note: FEW_CASES},
    age10to11: {name: 'Alter 10–11', value: '×1,3'},
    calls: {name: 'Anruf mit der Familie', value: '×1,3', companion: true},
    familyMessages: {name: 'Nachricht von der Familie', value: '×1,5', companion: true},
};

/** "×1,8", "×1,4–2,8 (wenige Fälle)", "kein eigener Effekt belegt" */
export function qualityFactorText(key: QualitySignalFactorKey): string {
    const {value, note} = QUALITY_SIGNAL_FACTORS[key];
    return value ? `${value}${note ? ` (${note})` : ''}` : note || '';
}

/** "1,8": the bare value of a factor for sentences like „1,8-mal so viele“ */
export function qualityFactorNumber(key: QualitySignalFactorKey): string {
    return (QUALITY_SIGNAL_FACTORS[key].value || '').replace('×', '');
}
